/* eslint-disable camelcase, jsx-a11y/no-noninteractive-element-interactions,@typescript-eslint/camelcase */
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';
import { StyledButton as LegacyButton } from '../../CommonComponents/Button';
import { IconButton } from '../../CommonComponents/OPL/IconButton/IconButton';
import { ListItem } from '../../CommonComponents/OPL/List/ListItem';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '../../CommonComponents/OPL/Menu/Menu';
import Spinner from '../../CommonComponents/Spinner';
import { Feature } from '../../CommonSelectors/CommonArea.types';
import getImagePath from '../../CommonUtils/getImagePath';
import { ContactAdvisor } from '../../ContactAdvisor/ContactAdvisor';
import { PaymentModal } from '../../Payment/Payment';
import { setIsOpen as openPaymentModal } from '../../Payment/state';
import { TwoFactorAuthenticationModal } from '../../TwoFactorAuthentication/TwoFactorAuthentication';
import { useFeatureFlag } from '../../_hooks/useFeatureFlag';
import { ClientAssociatedProfiles } from './components/ClientAssociatedProfiles/ClientAssociatedProfiles';
import { LanguagePreference } from './components/LanguagePreference/LanguagePreference';
import Help from './TopNavHelp';
import {
  Bar,
  ButtonWithBigIcon,
  DefaultLogo,
  MainNavigationLinks,
  NavigationLinkWithIcon,
  RightSideNavigation,
  SecondaryNavigationLinks,
  Wrapper,
} from './TopNavStyles';

const goToRoute = route => {
  const routeObj = ADV.util.Router.getObjFromHash(route);

  if (routeObj) {
    ADV.util.Router.go2(routeObj.id, routeObj.params);
  }
};

const UnStyledLi = styled.li`
  list-style: none;
`;

const TopNavView = ({
  initialize,
  currentClient,
  isConsumerArea,
  hasTwoFactorAuthentication,
  workflow,
  allow_full_screen,
  hasAccess,
  showContactAdviser,
  isNotInWorkflows,
  client,
  burgerMenuItems,
  hasMobileNavigation,
}) => {
  const useSimpleNav = hasMobileNavigation;

  const [fullPageBlock, setFullPageBlock] = useState(false);

  const [isTwoFactorAuthModalVisible, setIsTwoFactorAuthModalVisible] = useState(false);

  const dispatch = useDispatch();

  useEffectOnce(() => {
    if (currentClient) {
      initialize(currentClient, isConsumerArea);
    }
  });

  const showLanguagePreference = useFeatureFlag(Feature.LocalizeAccess);

  return (
    <header id="ConsumerAreaHeader" className="new-portal">
      {fullPageBlock && <Spinner />}

      <Bar>
        <Wrapper>
          {[
            !workflow && hasAccess && (
              <MainNavigationLinks>
                {isConsumerArea && !!ADV.info.WhiteLabel && (
                  <div className={`WhiteLabel${workflow ? ' is-workflow' : ''}`}>
                    {ADV.info.WhiteLabel.logo ? (
                      <img
                        src={ADV.settings.endpoints.whitelabelDir + ADV.info.WhiteLabel.logo}
                        alt={ADV.info.WhiteLabel.title}
                      />
                    ) : (
                      <DefaultLogo alt="Logo" src={getImagePath('orion-planning-logo.svg')} />
                    )}
                  </div>
                )}
              </MainNavigationLinks>
            ),
            <SecondaryNavigationLinks>
              {!isConsumerArea && (
                <NavigationLinkWithIcon>
                  <LegacyButton.Link
                    prependIcon="fas fa-file-download"
                    onClick={() => ADV.util.Router.go2('AdvancedPDF', { clientId: client.Id })}
                  >
                    PDF
                  </LegacyButton.Link>
                </NavigationLinkWithIcon>
              )}

              {!isConsumerArea && (
                <NavigationLinkWithIcon>
                  <LegacyButton.Link
                    prependIcon="fa fa-desktop"
                    onClick={() =>
                      window.open(
                        `${ADV.settings.endpoints.api}Consumer/Consumer?isPresentationMode=true&clientId=${client.Id}`,
                        '_blank',
                      )
                    }
                  >
                    Present
                  </LegacyButton.Link>
                </NavigationLinkWithIcon>
              )}

              {!useSimpleNav && isNotInWorkflows && isConsumerArea && showContactAdviser && (
                <ContactAdvisor />
              )}

              {!useSimpleNav && isConsumerArea && allow_full_screen && (
                <NavigationLinkWithIcon>
                  <ButtonWithBigIcon
                    prependIcon="fa fa-expand"
                    className="fullscreen"
                    onClick={e => {
                      e.target.classList.toggle('enabled');
                      ADV.util.toggleFullScreen();
                    }}
                    data-callout="fullscreen"
                  />
                </NavigationLinkWithIcon>
              )}

              {!useSimpleNav && isConsumerArea && currentClient.HelpText && <Help />}

              {isConsumerArea && (
                <RightSideNavigation>
                  <Menu>
                    <MenuTrigger>
                      <IconButton
                        size="large"
                        icon="fa fa-bars"
                        variant="subtleInactive"
                        aria-label="menu"
                      />
                    </MenuTrigger>

                    <MenuContent>
                      {!ADV.info.isPresentationMode && ADV.data.user.HasClientAssociatedProfilesAccess && (
                        <ClientAssociatedProfiles
                          clientId={client.Id}
                          onSwitchProfile={() => setFullPageBlock(true)}
                          onSwitchProfileError={() => setFullPageBlock(false)}
                        />
                      )}

                      {!hasMobileNavigation &&
                        burgerMenuItems.map(item => (
                          <MenuItem key={item.label}>
                            <UnStyledLi data-action={item.dataAction} data-params={item.dataParams}>
                              <ListItem icon={item.icon} onClick={() => goToRoute(item.route)}>
                                {item.label}
                              </ListItem>
                            </UnStyledLi>
                          </MenuItem>
                        ))}

                      {!useSimpleNav && burgerMenuItems.length > 0 && <MenuSeparator />}

                      {!useSimpleNav && hasTwoFactorAuthentication && (
                        <>
                          <MenuItem onSelect={() => setIsTwoFactorAuthModalVisible(true)}>
                            <ListItem icon="far fa-lock-alt">Two-Factor Authentication</ListItem>
                          </MenuItem>

                          <MenuSeparator />
                        </>
                      )}

                      {!useSimpleNav && ADV.data.user.HasBluePayAccess && (
                        <>
                          <MenuItem onSelect={() => dispatch(openPaymentModal(true))}>
                            <ListItem icon="far fa-credit-card">Payment</ListItem>
                          </MenuItem>

                          <MenuSeparator />
                        </>
                      )}

                      {!useSimpleNav && showLanguagePreference && (
                        <LanguagePreference clientId={client.Id} />
                      )}

                      <MenuItem onSelect={() => ADV.Controller.logout(true, false)}>
                        <ListItem icon="fa fa-sign-out-alt">Log Out</ListItem>
                      </MenuItem>
                    </MenuContent>
                  </Menu>
                </RightSideNavigation>
              )}
            </SecondaryNavigationLinks>,
          ]}
        </Wrapper>
      </Bar>

      <PaymentModal />

      {isTwoFactorAuthModalVisible && (
        <TwoFactorAuthenticationModal
          clientId={client.Id}
          onClose={() => setIsTwoFactorAuthModalVisible(false)}
        />
      )}
    </header>
  );
};

TopNavView.defaultProps = {
  workflow: undefined,
};

TopNavView.propTypes = {
  burgerMenuItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  client: PropTypes.shape({
    Id: PropTypes.string,
  }).isRequired,
  initialize: PropTypes.func.isRequired,
  currentClient: PropTypes.shape({
    HelpText: PropTypes.string,
  }).isRequired,
  isConsumerArea: PropTypes.bool.isRequired,
  hasTwoFactorAuthentication: PropTypes.bool.isRequired,
  workflow: PropTypes.shape({}),
  allow_full_screen: PropTypes.bool.isRequired,
  hasAccess: PropTypes.bool.isRequired,
  showContactAdviser: PropTypes.bool.isRequired,
  isNotInWorkflows: PropTypes.bool.isRequired,
  hasMobileNavigation: PropTypes.bool.isRequired,
};

export default TopNavView;
